import { Signer } from '@ethersproject/abstract-signer'
import { Provider } from '@ethersproject/providers'
import { BigNumber } from '@xylabs/bignumber'
import { EthAddress } from '@xylabs/sdk-js'
import { XyoWorldMinter, XyoWorldMinter__factory as XyoWorldMinterFactory } from '@xyo-network/world-typechain'

import { Quadkey } from '../../Quadkey'
import { XyoErc20Wrapper } from '../Erc20'
import { XyoEthersWrapperBase } from '../EthersBase'
import { XyoWorldGeotokensWrapper } from '../Geotokens'

export class XyoWorldMinterWrapper extends XyoEthersWrapperBase {
  public contract: XyoWorldMinter

  private _now?: number

  constructor(address: EthAddress, provider: Provider, signer?: Signer) {
    super(provider, signer)
    this.contract = XyoWorldMinterFactory.connect(address.toString(), this.signer ?? this.provider)
  }

  public async bid(quadkey: Quadkey, amount: BigNumber) {
    return await this.contract.bid(quadkey.toString(), amount.toString(10))
  }

  public async bidFee(quadkey: Quadkey, amount: BigNumber) {
    return XyoWorldMinterWrapper.toBigNumber(await this.contract.bidFee(quadkey.toString(), amount.toString(10)))
  }

  public async bidder(quadkey: Quadkey) {
    return EthAddress.fromString(await this.contract.bidder(quadkey.toString()))
  }

  public async canMintForReward(quadkey: Quadkey) {
    return await this.contract.canMintForReward(quadkey.toString())
  }

  public async currentBid(quadkey: Quadkey) {
    return XyoWorldMinterWrapper.toBigNumber(await this.contract.currentBid(quadkey.toString()))
  }

  public async dutchAuctionActive(quadkey: Quadkey) {
    return await this.contract.dutchAuctionActive(quadkey.toString())
  }

  public async dutchAuctionPrice(quadkey: Quadkey) {
    return XyoWorldMinterWrapper.toBigNumber(await this.contract.dutchAuctionPrice(quadkey.toString()))
  }

  public async endTime(quadkey: Quadkey) {
    return await this.contract.endTime(quadkey.toString())
  }

  public equals(obj: XyoWorldMinterWrapper): boolean {
    return obj.contract.address.toLowerCase() === this.contract.address.toLowerCase()
  }

  public async expired(quadkey: Quadkey) {
    return await this.contract.expired(quadkey.toString())
  }

  public async fetchNow() {
    return (await this.contract.getNow()).toNumber()
  }

  public async getCurrency() {
    const currencyAddress = EthAddress.fromString(await this.contract.currency())
    return currencyAddress ? new XyoErc20Wrapper(currencyAddress, this.provider, this.signer) : undefined
  }

  public async getNow() {
    this._now = this._now ?? (await this.fetchNow())
    return this._now
  }

  public async getTokens() {
    const currencyAddress = EthAddress.fromString(await this.contract.tokens())
    if (currencyAddress) {
      return new XyoWorldGeotokensWrapper(currencyAddress, this.provider, this.signer)
    }
  }

  public async hasBid(quadkey: Quadkey) {
    return await this.contract.hasBid(quadkey.toString())
  }

  public async minimumBid(quadkey: Quadkey) {
    return XyoWorldMinterWrapper.toBigNumber(await this.contract.minimumBid(quadkey.toString()))
  }

  public async mint(quadkey: Quadkey) {
    return await this.contract.mint(quadkey.toString(), { gasLimit: 1200000 })
  }

  public async mintForReward(quadkey: Quadkey) {
    return await this.contract.mintForReward(quadkey.toString(), {
      gasLimit: 1200000,
    })
  }

  public async mintNowFee(quadkey: Quadkey) {
    return XyoWorldMinterWrapper.toBigNumber(await this.contract.mintNowFee(quadkey.toString()))
  }

  public async mintNowPrice(quadkey: Quadkey) {
    return XyoWorldMinterWrapper.toBigNumber(await this.contract.mintNowPrice(quadkey.toString()))
  }

  public async minted(quadkey: Quadkey) {
    if (quadkey === Quadkey.root) {
      return true
    }
    return await this.contract.hasMinted(quadkey.toString())
  }

  public now() {
    return this._now
  }

  public async started(quadkey: Quadkey) {
    return await this.contract.started(quadkey.toString())
  }
}
